import React from 'react';
import ReactMarkdown from 'react-markdown';
import styled from '@emotion/styled';
import {
  animations,
  colors,
  typography,
  helpers,
  mediaQueries as mq,
} from 'src/styles';
import Image from 'src/components/Image';
import Link from 'src/components/Link';
import PropTypes from 'prop-types';

const Wrapper = styled.div`
  align-items: flex-start;
  display: flex;
`;
const ImageWrapper = styled.div`
  border-radius: 50%;
  flex-grow: 0;
  flex-shrink: 0;
  overflow: hidden;

  ${({ useResourceStyles }) =>
    !useResourceStyles
      ? `
    width: 25%;
    max-width: 200px;
  `
      : `
    width: 70px;
  `}
`;
const TextWrapper = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  margin-left: 20px;
  color: ${colors.textColor};

  ${mq.largeAndUp} {
    margin-left: 30px;
  }
`;
const Title = styled(ReactMarkdown)`
  ${({ useResourceStyles }) =>
    !useResourceStyles
      ? `
    ${typography.h4}
  `
      : `
    font-size: 16px;
    line-height: ${typography.leadingSnug};

    ${mq.largeAndUp} {
      font-size: ${typography.fontSizeExtraLarge2};
    }
  `}
  font-weight: 500;
`;
const Description = styled.p`
  ${({ useResourceStyles }) =>
    !useResourceStyles
      ? `
    ${helpers.responsiveStyles('margin-bottom', 32, 24, 20, 16)}
    ${mq.largeAndUp} {
      ${typography.bodyMedium}
    }
  `
      : `
    font-size: ${typography.fontSizeSmall};
    color: ${colors.textColor};
    margin: 5px 0 10px;
  `}
`;
const StyledLink = styled(Link)`
  ${({ useResourceStyles, mainColor }) =>
    !useResourceStyles
      ? `
    display: inline-block;
    text-align: center;
    padding: 7px 25px;
    color: ${colors.white};
    background-color: ${colors[mainColor]};
    min-width: 100px;
    font-size: 14px;
    font-weight: 500;
    transition: background-color ${animations.mediumSpeed} ease-in-out;

    ${mq.largeAndUp} {
      padding: 10px 25px;
      font-size: ${typography.fontSizeExtraLarge};
    }

    &:hover {
      background-color: ${colors[`${mainColor}Darken`]};
    }

  `
      : `
    transition: color ${animations.mediumSpeed} ease-in-out;
    font-size: 16px;
    color: ${colors[mainColor]};
    font-weight: 700;

    &:hover {
      color: ${colors[`${mainColor}Darken`]};
    }

    ${mq.largeAndUp} {
      font-size: ${typography.fontSizeLarge};
    }
  `}
`;

export default function ReadMoreLink({
  image,
  title,
  text,
  link,
  buttonText,
  useResourceStyles,
  mainColor,
}) {
  return (
    <Wrapper>
      <ImageWrapper useResourceStyles={useResourceStyles}>
        <Image image={{ image }} animate={false} />
      </ImageWrapper>
      <TextWrapper>
        <Title
          useResourceStyles={useResourceStyles}
          children={title}
          components={{ p: 'span' }}
        />
        <Description useResourceStyles={useResourceStyles}>{text}</Description>
        {buttonText && (
          <StyledLink
            to={link}
            mainColor={mainColor}
            useResourceStyles={useResourceStyles}
          >
            <ReactMarkdown children={buttonText} components={{ p: 'span' }} />
          </StyledLink>
        )}
      </TextWrapper>
    </Wrapper>
  );
}

ReadMoreLink.defaultProps = {
  useResourceStyles: false,
  mainColor: 'green',
};

ReadMoreLink.propTypes = {
  image: PropTypes.object,
  title: PropTypes.string,
  text: PropTypes.string,
  link: PropTypes.string,
  buttonText: PropTypes.string,
  useResourceStyles: PropTypes.bool,
  mainColor: PropTypes.string,
};
